import React, { useEffect, useRef, useState } from "react";

export function GlowingEffect({
  spread = 15, // Spread for the glow effect
  glow = true,
  disabled = false,
  proximity = 10,
  inactiveZone = 0.1,
  borderWidth = 0, // No border to avoid shifting
}) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const targetOpacityRef = useRef(0);
  const animationFrameId = useRef(null);
  const lastUpdateTime = useRef(0);

  // Throttled position update for smoother animation
  const updatePosition = (time) => {
    if (!lastUpdateTime.current) {
      lastUpdateTime.current = time;
    }
    
    const deltaTime = time - lastUpdateTime.current;
    
    // Limit updates to 60fps equivalent for smoothness
    if (deltaTime > 16) {
      lastUpdateTime.current = time;
      
      if (containerRef.current) {
        // Smooth the position updates with lerp (linear interpolation)
        setPosition(prev => ({
          x: prev.x + (mousePositionRef.current.x - prev.x) * 0.2,
          y: prev.y + (mousePositionRef.current.y - prev.y) * 0.2
        }));
        
        // Smooth opacity transitions
        setOpacity(prev => prev + (targetOpacityRef.current - prev) * 0.15);
      }
    }
    
    animationFrameId.current = requestAnimationFrame(updatePosition);
  };

  useEffect(() => {
    animationFrameId.current = requestAnimationFrame(updatePosition);
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current && !disabled) {
        const rect = containerRef.current.getBoundingClientRect();
        
        // Mouse position relative to viewport
        const mouseX = e.clientX;
        const mouseY = e.clientY;
        
        // Calculate position relative to element
        const x = mouseX - rect.left;
        const y = mouseY - rect.top;
        
        // Calculate distance from element edges
        const distanceFromLeft = mouseX - rect.left;
        const distanceFromRight = rect.right - mouseX;
        const distanceFromTop = mouseY - rect.top;
        const distanceFromBottom = rect.bottom - mouseY;
        
        // Find minimum distance to any edge
        const minHorizontalDistance = Math.min(distanceFromLeft, distanceFromRight);
        const minVerticalDistance = Math.min(distanceFromTop, distanceFromBottom);
        
        // Check if mouse is inside the element
        const isInsideElement = 
          mouseX >= rect.left && 
          mouseX <= rect.right && 
          mouseY >= rect.top && 
          mouseY <= rect.bottom;
        
        // Set mouse position for the gradient
        mousePositionRef.current = { x, y };
        
        // Calculate distance from box (0 if inside)
        let distanceFromBox = 0;
        
        if (!isInsideElement) {
          // If outside, calculate distance from closest edge
          if (mouseX < rect.left) {
            // Left of element
            if (mouseY < rect.top) {
              // Top-left corner
              distanceFromBox = Math.sqrt(
                Math.pow(rect.left - mouseX, 2) + 
                Math.pow(rect.top - mouseY, 2)
              );
            } else if (mouseY > rect.bottom) {
              // Bottom-left corner
              distanceFromBox = Math.sqrt(
                Math.pow(rect.left - mouseX, 2) + 
                Math.pow(mouseY - rect.bottom, 2)
              );
            } else {
              // Directly left
              distanceFromBox = rect.left - mouseX;
            }
          } else if (mouseX > rect.right) {
            // Right of element
            if (mouseY < rect.top) {
              // Top-right corner
              distanceFromBox = Math.sqrt(
                Math.pow(mouseX - rect.right, 2) + 
                Math.pow(rect.top - mouseY, 2)
              );
            } else if (mouseY > rect.bottom) {
              // Bottom-right corner
              distanceFromBox = Math.sqrt(
                Math.pow(mouseX - rect.right, 2) + 
                Math.pow(mouseY - rect.bottom, 2)
              );
            } else {
              // Directly right
              distanceFromBox = mouseX - rect.right;
            }
          } else {
            // Mouse is directly above or below the element
            if (mouseY < rect.top) {
              // Above
              distanceFromBox = rect.top - mouseY;
            } else {
              // Below
              distanceFromBox = mouseY - rect.bottom;
            }
          }
        }
        
        // If distance is within proximity, show glow
        if (distanceFromBox <= proximity || isInsideElement) {
          if (!isHovering) {
            setIsHovering(true);
          }
          
          // Calculate opacity based on distance from box
          let opacityFactor = isInsideElement ? 1 : Math.max(0, 1 - (distanceFromBox / proximity));
          
          // Apply non-linear curve for smoother transition
          targetOpacityRef.current = Math.pow(opacityFactor, 1.2) - inactiveZone;
        } else {
          if (isHovering) {
            setIsHovering(false);
          }
          targetOpacityRef.current = 0;
        }
      }
    };
    
    const handleMouseLeave = () => {
      setIsHovering(false);
      targetOpacityRef.current = 0;
    };
    
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [disabled, proximity, inactiveZone, isHovering]);

  // Glow size and blur values
  const glowSize = Math.floor(spread * 0.6);
  const blurRadius = Math.floor(spread * 0.9);
  
  // Define a consistent glow color to use for all sides
  const glowColor = 'rgba(254, 151, 0, 0.5)';
  
  return (
    <div
      ref={containerRef}
      className="glowing-effect absolute inset-0 rounded-[inherit] pointer-events-none"
      style={{
        "--border-width": `${borderWidth}px`,
      }}
    >
      {/* Top-left */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `-${glowSize}px -${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity", 
        }}
      />
      
      {/* Top */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `0 -${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Top-right */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `${glowSize}px -${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Right */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `${glowSize}px 0 ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Bottom-right */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `${glowSize}px ${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Bottom */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `0 ${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Bottom-left */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `-${glowSize}px ${glowSize}px ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
      
      {/* Left */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          opacity: glow ? opacity * 0.7 : 0,
          boxShadow: `-${glowSize}px 0 ${blurRadius}px ${glowColor}`,
          pointerEvents: "none",
          willChange: "opacity",
        }}
      />
    </div>
  );
} 